import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import {
  MagnifyingGlassIcon,
  PhoneIcon,
  UserPlusIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeOpenIcon,
  CalendarDaysIcon,
  LinkIcon,
  FunnelIcon,
  MapPinIcon,
  PencilSquareIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { fetchCustomers, createCustomer, createCustomerCommunication, fetchCustomerCommunications, updateCustomerCommunication, fetchCustomerEvents, fetchAllCommunications } from "../../../api/customer";
import { fetchCommuncationPdf } from "../../../api/customer/communcations";
import { useReportLockedCustomer } from "../../../contexts/CustomerLockTracker";
import { CustomerLockBanner } from "../../../components/dashboard/customers/CustomerLockBanner";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { fetchReports, fetchReport } from "../../../api/service/report";
import { fetchCases, createCaseItem } from "../../../api/case";
import { toast } from "react-hot-toast";
import RoleOnly from "../../../routes/roleOnly";
import { format } from "date-fns";
import CaseDialog from "../../../components/dashboard/cases/case-dialog";
import ReportOverviewSummary from "../../../components/dashboard/service/reports/ReportOverviewSummary";
import { PMField } from "../../../components/utils/PMField";
import CustomerDialog from "../../../components/dashboard/customers/dialog";

const CustomerHandler = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchError, setSearchError] = useState(null); // Track search errors
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [exportingCommPdf, setExportingCommPdf] = useState(false);
  useReportLockedCustomer(
    selectedCustomer?.id ? `customer-handler-${selectedCustomer.id}` : "customer-handler",
    selectedCustomer
  );
  const [customerData, setCustomerData] = useState({
    communications: [],
    cases: [],
    events: [],
    loading: false,
  });
  
  // State for selected communications and dialogs
  const [selectedCommunications, setSelectedCommunications] = useState([]);
  const [communicationDetailDialog, setCommunicationDetailDialog] = useState(null);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [caseChoiceDialogOpen, setCaseChoiceDialogOpen] = useState(false);
  const [caseSearchDialogOpen, setCaseSearchDialogOpen] = useState(false);
  const [caseSearchQuery, setCaseSearchQuery] = useState("");
  const [caseSearchResults, setCaseSearchResults] = useState([]);
  const [isSearchingCases, setIsSearchingCases] = useState(false);
  const [customerCasesLoaded, setCustomerCasesLoaded] = useState(false);
  const [isLinkingToCase, setIsLinkingToCase] = useState(false);

  // Report dialog state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [customerReports, setCustomerReports] = useState([]);
  const [isLoadingCustomerReports, setIsLoadingCustomerReports] = useState(false);
  
  // State for treatment section (all communications)
  const [allCommunications, setAllCommunications] = useState([]);
  const [isLoadingAllCommunications, setIsLoadingAllCommunications] = useState(false);
  const [communicationSearchQuery, setCommunicationSearchQuery] = useState("");
  const [communicationFilter, setCommunicationFilter] = useState("all"); // all, processed, unprocessed
  const [communicationChannelFilter, setCommunicationChannelFilter] = useState("all"); // all, phone, email, sms
  const [pendingCommunicationSelection, setPendingCommunicationSelection] = useState(null); // Communication ID to auto-select after customer loads
  const [isLoadingCommunication, setIsLoadingCommunication] = useState(false); // Loading state when clicking on communication in treatment section
  const [customerEditDialogOpen, setCustomerEditDialogOpen] = useState(false);
  const [editingCommunicationId, setEditingCommunicationId] = useState(null);
  const [communicationEditDraft, setCommunicationEditDraft] = useState({ subject: "", body_text: "" });
  const [isSavingCommunicationEdit, setIsSavingCommunicationEdit] = useState(false);

  const isOwnCustomerHandlerCommunication = (comm) => {
    if (!comm || currentUser?.id == null) return false;
    const meta = comm.metadata || {};
    return (
      meta.source === "customer_handler" &&
      Number(meta.handler_id) === Number(currentUser.id)
    );
  };

  const openCustomerProfileInNewTab = (customerId) => {
    if (!customerId) return;
    window.open(`/dashboard/customers/${customerId}`, "_blank", "noopener,noreferrer");
  };

  const formatCustomerAddressLine = (customer) => {
    if (!customer) return null;
    const street = customer.address?.trim();
    const cityLine = [customer.postal, customer.place].filter(Boolean).join(" ").trim();
    const parts = [street, cityLine].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  };

  const handleCustomerEditDialogClose = async () => {
    setCustomerEditDialogOpen(false);
    const id = selectedCustomer?.id;
    if (!id) return;
    try {
      const res = await fetchCustomers({
        ids: id.toString(),
        per_page: 1,
        page: 1,
      });
      if (res?.data?.length) {
        setSelectedCustomer(res.data[0]);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Reset function to clear all state and start fresh, but reload treatment section
  const handleReset = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
    setSearchError(null);
    setSelectedCustomer(null);
    setCustomerData({
      communications: [],
      cases: [],
      events: [],
      loading: false,
    });
    setSelectedCommunications([]);
    setCommunicationDetailDialog(null);
    setCaseDialogOpen(false);
    setSelectedCase(null);
    setEventDialogOpen(false);
    setSelectedEvent(null);
    setCaseChoiceDialogOpen(false);
    setCaseSearchDialogOpen(false);
    setCaseSearchQuery("");
    setCaseSearchResults([]);
    setIsSearchingCases(false);
    setCustomerCasesLoaded(false);
    setIsLinkingToCase(false);
    // Keep allCommunications and reload treatment section
    setCommunicationSearchQuery("");
    setCommunicationFilter("all");
    setCommunicationChannelFilter("all");
    setPendingCommunicationSelection(null);
    setIsLoadingCommunication(false);
    setCustomerEditDialogOpen(false);
    setEditingCommunicationId(null);
    setCommunicationEditDraft({ subject: "", body_text: "" });
    setIsSavingCommunicationEdit(false);
    setReportDialogOpen(false);
    setSelectedReportId(null);
    setCustomerReports([]);
    setIsLoadingCustomerReports(false);
    setFormData({
      name: "",
      mobile: "",
      address: "",
      postal: "",
      place: "",
      email: "",
      conversation_date: new Date().toISOString().slice(0, 16), // Default to now
      conversation_time: new Date().toTimeString().slice(0, 5),
      note: "",
      status: "pending",
      processed: false,
    });
    setShowCreateForm(false);
    loadAllCommunications();
  };

  // Helper function to filter out empty objects from arrays
  const filterEmptyObjects = (array) => {
    if (!Array.isArray(array)) return [];
    return array.filter(item => {
      // Filter out empty objects, null, undefined
      if (!item || typeof item !== 'object') return false;
      // Check if object has any keys
      return Object.keys(item).length > 0;
    });
  };

  // Form state for new customer/conversation
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    address: "",
    postal: "",
    place: "",
    email: "",
    conversation_date: new Date().toISOString().slice(0, 16), // Default to now
    conversation_time: new Date().toTimeString().slice(0, 5),
    note: "",
    status: "pending", // pending or handled
    processed: false, // Whether communication is processed
  });

  // Search for customer by mobile or address
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null); // Clear previous errors

    // Update form data with current date/time for fresh search
    setFormData((prev) => ({
      ...prev,
      conversation_date: new Date().toISOString().slice(0, 16),
      conversation_time: new Date().toTimeString().slice(0, 5),
    }));

    try {
      // First, try exact match search (mobile number or address)
      // Backend supports exact_match parameter for exact match only
      const response = await fetchCustomers({
        query: searchQuery,
        exact_match: true, // Exact match only - skips fuzzy search
        per_page: 10,
        page: 1,
      });

      // Check if response is an error (when fetchCustomers catches an error, it returns error.response.data)
      // Error responses typically don't have a 'data' property or have 'error'/'errors' properties
      const isErrorResponse = !response || 
                              response.error || 
                              response.errors || 
                              (response.status && response.status >= 400) ||
                              (!response.data && !Array.isArray(response));

      if (isErrorResponse) {
        const errorMessage = response?.errors?.[0] || 
                            response?.message || 
                            response?.error || 
                            t("search.failed.server.error");
        setSearchError(errorMessage);
        toast.error(t("search.failed.server.error.message"), {
          duration: 5000,
        });
        setSearchResults([]);
        setSelectedCustomer(null);
        setShowCreateForm(false); // DO NOT show create form on error
        return;
      }

      // Check if we have valid data
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const customers = response.data;
        setSelectedCustomer(customers[0]);
        // Ikke vis søkeresultat-liste når treff = 1 (kunden velges direkte)
        setSearchResults(customers.length > 1 ? customers : []);
        setShowCreateForm(false);
        setSearchError(null);
      } else if (response.data && Array.isArray(response.data) && response.data.length === 0) {
        // No results - show create form (only if search was successful but no matches)
        setSearchResults([]);
        setSelectedCustomer(null);
        setShowCreateForm(true);
        setSearchError(null);
        // Pre-fill mobile if it looks like a phone number
        if (/^[\d\s+()-]+$/.test(searchQuery)) {
          setFormData((prev) => ({ ...prev, mobile: searchQuery }));
        } else {
          // Might be an address
          setFormData((prev) => ({ ...prev, address: searchQuery }));
        }
      } else {
        // Unexpected response format - treat as error
        setSearchError(t("search.failed.unexpected.response"));
        toast.error(t("search.failed.server.error.message"), {
          duration: 5000,
        });
        setSearchResults([]);
        setSelectedCustomer(null);
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error("Search error:", error);
      const errorMessage = error.response?.data?.errors?.[0] || 
                          error.response?.data?.message || 
                          error.message || 
                          t("search.failed.server.error");
      setSearchError(errorMessage);
      toast.error(t("search.failed.server.error.message"), {
        duration: 5000,
      });
      setSearchResults([]);
      setSelectedCustomer(null);
      setShowCreateForm(false); // DO NOT show create form on error
    } finally {
      setIsSearching(false);
    }
  };

  // Create new customer/conversation
  const handleCreateConversation = async (data = null) => {
    // Use provided data or fall back to formData state
    const formValues = data || formData;
    
    if (!formValues.name && !formValues.mobile) {
      toast.error(t("name.or.mobile.required"));
      return;
    }

    try {
      // Create customer
      const customerData = {
        name: formValues.name || t("unknown.customer"),
        mobile: formValues.mobile,
        email: formValues.email || "",
        address: formValues.address || "",
        postal: formValues.postal || "",
        place: formValues.place || "",
        active: true,
      };

      const customerResponse = await createCustomer(customerData);
      const newCustomer = customerResponse;

      if (newCustomer && newCustomer.id) {
        // Create customer communication (if note is provided)
        const noteValue = formValues.note?.trim();

        if (noteValue) {
          try {
            // Combine date and time for sent_at
            const conversationDate = formValues.conversation_date || formData.conversation_date;
            const conversationTime = formValues.conversation_time || formData.conversation_time;
            
            // Handle both date formats (with or without T separator)
            const dateStr = conversationDate?.includes('T') 
              ? conversationDate.split('T')[0] 
              : conversationDate;
            
            const sentAt = dateStr && conversationTime
              ? new Date(`${dateStr}T${conversationTime}`).toISOString()
              : new Date().toISOString();
            
            const communicationData = {
              channel: "phone",
              direction: "inbound", // Assuming inbound calls from customers
              subject: "Telefonsamtale med kunde",
              body_text: noteValue,
              sent_at: sentAt,
              processed: formValues.processed || false, // Use processed from form
              metadata: {
                conversation_date: dateStr || null,
                conversation_time: conversationTime || null,
                status: formValues.processed ? 'handled' : 'pending',
                handler: currentUser?.name || currentUser?.email || 'Ukjent',
                handler_id: currentUser?.id || null,
                source: "customer_handler", // Mark that this came from customer handler
                mobile: formValues.mobile || null,
              },
            };
            

            const commResponse = await createCustomerCommunication(newCustomer.id, communicationData);
 
          } catch (communicationError) {
            console.error("Communication creation error:", communicationError);
            console.error("Error details:", communicationError.response?.data || communicationError.message);
            toast.error(t("failed.to.create.communication"));
            // Don't fail the whole operation if communication fails
          }
        } else {
          console.log("No note provided, skipping communication creation");
        }

        toast.success(
          formValues.processed
            ? t("conversation.saved.and.handled")
            : t("conversation.saved")
        );

        // Reset form
        setFormData({
          name: "",
          mobile: "",
          address: "",
          postal: "",
          place: "",
          email: "",
          conversation_date: new Date().toISOString().slice(0, 16),
          conversation_time: new Date().toTimeString().slice(0, 5),
          note: "",
          status: "pending",
          processed: false,
        });
        setSearchQuery("");
        setShowCreateForm(false);
        setSelectedCustomer(newCustomer);
        
        // Reload treatment section to show the new communication if one was created
        if (noteValue) {
          loadAllCommunications();
        }
      }
    } catch (error) {
      console.error("Create error:", error);
      toast.error(t("failed.to.create.customer"));
    }
  };

  // Fetch related data when customer is selected
  useEffect(() => {
    if (!selectedCustomer?.id) {
      setCustomerData({ communications: [], cases: [], events: [], loading: false });
      setCustomerReports([]);
      return;
    }

    // Prevent duplicate fetching
    let isMounted = true;

    const fetchCustomerData = async () => {
      setCustomerData((prev) => ({ ...prev, loading: true }));
      // Parallel loads with allSettled so one reject (Goal2 API contract) does not wipe every list.
      const [commResult, casesResult, eventsResult, reportsResult] = await Promise.allSettled([
        fetchCustomerCommunications(selectedCustomer.id, {
          per_page: 10,
          page: 1,
          sortBy: "sent_at",
          sort: "desc",
          fields: "id,channel,direction,subject,body_text,sent_at,processed,metadata,processed_by_user",
        }),
        fetchCases({
          filters: { 0: { name: "customer", value: selectedCustomer.id.toString() } },
          per_page: 10,
          page: 1,
          sortBy: "created_at",
          sort: "desc",
          fields: "id,case_number,displayTitle,title,status",
        }),
        fetchCustomerEvents(selectedCustomer.id, {
          per_page: 20,
          page: 1,
          sortBy: "start_time",
          sort: "desc",
          fields: "id,title,start_time,status,related_type,related,module_details,module_type,module_id",
        }),
        fetchReports({
          filters: { 0: { name: "customer", value: selectedCustomer.id.toString() } },
          per_page: 50,
          page: 1,
          sortBy: "created_at",
          sort: "desc",
          fields: "id,displayTitle,report_type,report_number,status,approval_status,agreement_date,description,created_at",
        }),
      ]);

      const settledList = (result, label) => {
        if (result.status === "fulfilled") {
          return filterEmptyObjects(result.value?.data || []);
        }
        console.error(`Error fetching customer ${label}:`, result.reason);
        return [];
      };

      const communications = settledList(commResult, "communications");
      const cases = settledList(casesResult, "cases");
      const events = settledList(eventsResult, "events");
      const reports = settledList(reportsResult, "reports");

      if (!isMounted) return;

      setCustomerData({
        communications,
        cases,
        events,
        loading: false,
      });
      setCustomerReports(reports);

      if (pendingCommunicationSelection) {
        if (commResult.status === "fulfilled") {
          const commExists = communications.some((c) => c.id === pendingCommunicationSelection);
          if (commExists) {
            setSelectedCommunications([pendingCommunicationSelection]);
          }
        }
        setPendingCommunicationSelection(null);
      }
    };

    fetchCustomerData();
    
    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isMounted = false;
    };
  }, [selectedCustomer?.id, pendingCommunicationSelection]);

  // Toggle communication selection
  const toggleCommunicationSelection = (commId) => {
    setSelectedCommunications((prev) => {
      if (prev.includes(commId)) {
        return prev.filter((id) => id !== commId);
      } else {
        return [...prev, commId];
      }
    });
  };

  const handleSaveCommunicationEdit = async () => {
    if (!selectedCustomer?.id || !editingCommunicationId) return;
    setIsSavingCommunicationEdit(true);
    try {
      const res = await updateCustomerCommunication(selectedCustomer.id, editingCommunicationId, {
        subject: communicationEditDraft.subject || "",
        body_text: communicationEditDraft.body_text || "",
      });
      if (res?.errors?.length) {
        toast.error(res.errors[0]);
        return;
      }
      if (res?.id) {
        toast.success(t("customer.handler.communication.update.success"));
        setCustomerData((prev) => ({
          ...prev,
          communications: prev.communications.map((c) =>
            c.id === res.id ? { ...c, ...res } : c
          ),
        }));
        setEditingCommunicationId(null);
        setCommunicationEditDraft({ subject: "", body_text: "" });
        loadAllCommunications();
      } else {
        toast.error(t("failed.to.update.communication"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("failed.to.update.communication"));
    } finally {
      setIsSavingCommunicationEdit(false);
    }
  };

  // Mark communication as processed
  const handleMarkAsProcessed = async (commId, processed) => {
    if (!selectedCustomer) return;
    
    try {
      const response = await updateCustomerCommunication(selectedCustomer.id, commId, {
        processed: processed,
      });
      
      // Update the specific communication in state directly from response
      // Response is the communication object directly (not wrapped in { data: ... })
      if (response && response.id) {
        setCustomerData((prev) => ({
          ...prev,
          communications: prev.communications.map((comm) =>
            comm.id === commId ? { ...comm, ...response } : comm
          ),
        }));
      } else {
        // Fallback: refresh all communications if response format is unexpected
        const commResponse = await fetchCustomerCommunications(selectedCustomer.id, {
          per_page: 10,
          page: 1,
          sortBy: "sent_at",
          sort: "desc",
          fields: "id,channel,direction,subject,body_text,sent_at,processed,metadata,processed_by_user",
        });
        
        setCustomerData((prev) => ({
          ...prev,
          communications: filterEmptyObjects(commResponse.data || []),
        }));
      }
      
      // Reload treatment section to reflect the change
      loadAllCommunications();
      
      toast.success(processed ? t("communication.marked.processed") : t("communication.marked.unprocessed"));
    } catch (error) {
      console.error("Error updating communication:", error);
      toast.error(t("failed.to.update.communication"));
    }
  };

  // Load customer's cases when dialog opens
  useEffect(() => {
    if (caseSearchDialogOpen && selectedCustomer?.id && !customerCasesLoaded) {
      // Load customer's cases automatically
      const loadCustomerCases = async () => {
        try {
          const response = await fetchCases({
            filters: { 0: { name: "customer", value: selectedCustomer.id.toString() } },
            per_page: 20,
            page: 1,
            sortBy: "created_at",
            sort: "desc",
            fields: "id,case_number,displayTitle,title,status",
          });
          
          const customerCases = filterEmptyObjects(response.data || []);
          setCaseSearchResults(customerCases);
          setCustomerCasesLoaded(true);
        } catch (error) {
          console.error("Error loading customer cases:", error);
        }
      };
      
      loadCustomerCases();
    }
    
    // Reset when dialog closes
    if (!caseSearchDialogOpen) {
      setCustomerCasesLoaded(false);
      setCaseSearchQuery("");
    }
  }, [caseSearchDialogOpen, selectedCustomer?.id, customerCasesLoaded]);

  // Search for existing cases (when user searches)
  const handleSearchCases = async () => {
    if (!caseSearchQuery.trim()) {
      // If search is empty, show customer's cases again
      if (selectedCustomer?.id) {
        try {
          const response = await fetchCases({
            filters: { 0: { name: "customer", value: selectedCustomer.id.toString() } },
            per_page: 20,
            page: 1,
            sortBy: "created_at",
            sort: "desc",
            fields: "id,case_number,displayTitle,title,status",
          });
          setCaseSearchResults(filterEmptyObjects(response.data || []));
        } catch (error) {
          console.error("Error loading customer cases:", error);
        }
      }
      return;
    }

    setIsSearchingCases(true);
    try {
      const response = await fetchCases({
        query: caseSearchQuery,
        per_page: 20,
        page: 1,
        sortBy: "created_at",
        sort: "desc",
        fields: "id,case_number,displayTitle,title,status",
      });
      
      setCaseSearchResults(filterEmptyObjects(response.data || []));
    } catch (error) {
      console.error("Error searching cases:", error);
      toast.error(t("search.failed"));
      setCaseSearchResults([]);
    } finally {
      setIsSearchingCases(false);
    }
  };

  // Link selected communications to a case
  const handleLinkToCase = async (caseId, openInNewTab = false) => {
    if (selectedCommunications.length === 0) {
      toast.error(t("no.communications.selected"));
      return;
    }
    
    // Prevent multiple clicks
    if (isLinkingToCase) {
      return;
    }
    
    setIsLinkingToCase(true);
    try {
      // Get communication details before updating
      const commsToLink = customerData.communications.filter(comm => 
        selectedCommunications.includes(comm.id)
      );
      
      // Update each selected communication to link to the case and mark as processed
      for (const commId of selectedCommunications) {
        await updateCustomerCommunication(selectedCustomer.id, commId, {
          related_type: "Dashboard::Case",
          related_id: caseId,
          processed: true, // Mark as processed when linked to case
        });
      }
      
      // Create case items for each linked communication (similar to PhoneroCall)
      for (const comm of commsToLink) {
        try {
          await createCaseItem({
            case_id: caseId,
            title: "Kommunikasjon lenket",
            description: comm.body_text || comm.subject || "Kommunikasjon lenket til sak",
            action: "LINKED",
            module: "customer_communication",
            module_param: comm.id,
            user_type: "user",
            user_id: currentUser?.id,
          });
        } catch (itemError) {
          console.error("Error creating case item for communication:", itemError);
          // Don't fail the whole operation if case item creation fails
        }
      }
      
      // Refresh communications
      const commResponse = await fetchCustomerCommunications(selectedCustomer.id, {
        per_page: 10,
        page: 1,
        sortBy: "sent_at",
        sort: "desc",
        fields: "id,channel,direction,subject,body_text,sent_at,processed,metadata,processed_by_user",
      });
      
      setCustomerData((prev) => ({
        ...prev,
        communications: filterEmptyObjects(commResponse.data || []),
      }));
      
      // Reload treatment section to reflect the processed status change
      loadAllCommunications();
      
      setSelectedCommunications([]);
      toast.success(t("communications.linked.to.case"));
      
      // Navigate to case view in new tab if requested
      if (openInNewTab && caseId) {
        const caseUrl = `/dashboard/cases/${caseId}`;
        window.open(caseUrl, '_blank');
      }
    } catch (error) {
      console.error("Error linking communications to case:", error);
      toast.error(t("failed.to.link.communications"));
    } finally {
      setIsLinkingToCase(false);
    }
  };

  // Add communication to existing customer
  const handleAddCommunication = async () => {
    if (!formData.note || !selectedCustomer) return;

    try {
      // Combine date and time for sent_at
      const sentAt = formData.conversation_date && formData.conversation_time
        ? new Date(`${formData.conversation_date.split('T')[0]}T${formData.conversation_time}`).toISOString()
        : new Date().toISOString();
      
      await createCustomerCommunication(selectedCustomer.id, {
        channel: "phone",
        direction: "inbound", // Assuming inbound calls from customers
        subject: "Telefonsamtale med kunde",
        body_text: formData.note,
        sent_at: sentAt,
        processed: formData.processed, // Use processed from form
        metadata: {
          conversation_date: formData.conversation_date?.split('T')[0] || null,
          conversation_time: formData.conversation_time || null,
          status: formData.processed ? 'handled' : 'pending',
          handler: currentUser?.name || currentUser?.email || 'Ukjent',
          handler_id: currentUser?.id || null,
          source: "customer_handler", // Mark that this came from customer handler
          mobile: selectedCustomer.mobile || null,
        },
      });

      toast.success(t("communication.added"));
      setFormData((prev) => ({ ...prev, note: "", processed: false }));
      
      // Refresh communications list
      const commResponse = await fetchCustomerCommunications(selectedCustomer.id, {
        per_page: 10,
        page: 1,
        sortBy: "sent_at",
        sort: "desc",
        fields: "id,channel,direction,subject,body_text,sent_at,processed,metadata,processed_by_user",
      });
      
      setCustomerData((prev) => ({
        ...prev,
        communications: filterEmptyObjects(commResponse.data || []),
      }));
      
      // Reload treatment section to show the new communication
      loadAllCommunications();
    } catch (error) {
      console.error("Communication creation error:", error);
      toast.error(t("failed.to.add.communication"));
    }
  };

  // Fetch all communications for treatment section
  const loadAllCommunications = async () => {
    setIsLoadingAllCommunications(true);
    try {
      // Build filters object for API
      const filters = {};
      let filterIndex = 0;
      
      // Add processed filter if not "all"
      if (communicationFilter === "processed") {
        filters[filterIndex] = { name: "processed", value: true };
        filterIndex++;
      } else if (communicationFilter === "unprocessed") {
        filters[filterIndex] = { name: "processed", value: false };
        filterIndex++;
      }
      
      // Add channel filter if not "all"
      if (communicationChannelFilter !== "all") {
        filters[filterIndex] = { name: "channel", value: communicationChannelFilter };
        filterIndex++;
      }
      
      // Build API params
      const apiParams = {
        per_page: 100,
        page: 1,
        sortBy: "sent_at",
        sort: "desc",
        fields: "id,channel,subject,body_text,sent_at,processed,customer",
      };
      
      // Add filters if any
      if (Object.keys(filters).length > 0) {
        apiParams.filters = filters;
      }
      
      // Add search query if present
      if (communicationSearchQuery) {
        apiParams.query = communicationSearchQuery;
      }
      
      // Fetch communications from the global endpoint with filters
      const response = await fetchAllCommunications(apiParams);
      
      const allComms = filterEmptyObjects(response.data || []);
      setAllCommunications(allComms);
    } catch (error) {
      console.error("Error loading all communications:", error);
      toast.error(t("failed.to.load.communications"));
    } finally {
      setIsLoadingAllCommunications(false);
    }
  };
  
  // Load communications on mount and when filters change
  useEffect(() => {
    loadAllCommunications();
  }, [communicationFilter, communicationChannelFilter]);
  
  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAllCommunications();
    }, 500); // Wait 500ms after user stops typing
    
    return () => clearTimeout(timeoutId);
  }, [communicationSearchQuery]);

  // Filter communications (processed and channel filters are handled by API, 
  // but we still filter by customer info search on frontend since API search is on subject/body_text only)
  const filteredCommunications = allCommunications.filter((comm) => {
    // Search filter - filter by customer info on frontend since API search only searches subject/body_text
    if (communicationSearchQuery) {
      const query = communicationSearchQuery.toLowerCase();
      const matchesSearch = 
        comm.subject?.toLowerCase().includes(query) ||
        comm.body_text?.toLowerCase().includes(query) ||
        comm.customer?.displayTitle?.toLowerCase().includes(query) ||
        comm.customer?.name?.toLowerCase().includes(query) ||
        comm.customer?.mobile?.includes(query);
      if (!matchesSearch) return false;
    }
    
    // Processed and channel filters are now handled by API, so no need to filter here
    // But we keep this as a safety check in case API doesn't filter correctly
    if (communicationFilter === "processed" && !comm.processed) return false;
    if (communicationFilter === "unprocessed" && comm.processed) return false;
    if (communicationChannelFilter !== "all" && comm.channel !== communicationChannelFilter) return false;
    
    return true;
  });

  // Open report dialog
  const handleOpenReportDialog = (reportId) => {
    if (!reportId) return;
    setSelectedReportId(reportId);
    setReportDialogOpen(true);
  };

  // Close report dialog
  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setSelectedReportId(null);
  };

  // Handle communication selection from treatment section
  const handleSelectCommunicationForTreatment = async (comm) => {
    if (!comm.customer?.id) {
      toast.error(t("customer.not.found"));
      return;
    }
    
    // Prevent multiple clicks
    if (isLoadingCommunication) {
      return;
    }
    
    setIsLoadingCommunication(true);
    
    // Load full customer data (customer object from communication might be minimal)
    try {
      const customerResponse = await fetchCustomers({
        ids: comm.customer.id.toString(),
        per_page: 1,
        page: 1,
      });
      
      if (customerResponse.data && customerResponse.data.length > 0) {
        const customer = customerResponse.data[0];
        
        // Set pending communication selection before setting customer
        // This will trigger the useEffect to auto-select it after data loads
        if (comm.id) {
          setPendingCommunicationSelection(comm.id);
        }
        
        setSelectedCustomer(customer);
        setSearchQuery("");
        setSearchResults([]);
        setShowCreateForm(false);
        
        toast.success(t("customer.loaded"));
      } else {
        toast.error(t("customer.not.found"));
      }
    } catch (error) {
      console.error("Error loading customer:", error);
      toast.error(t("failed.to.load.customer"));
      setPendingCommunicationSelection(null);
    } finally {
      setIsLoadingCommunication(false);
    }
  };

  return (
    <RoleOnly role="customer-handler" action="read">
      <div className="min-h-screen bg-gray-50">
        <div className="left-10 max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Customer Search and Management */}
            <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t("customer.handler")}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {t("customer.handler.description")}
              </p>
            </div>
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Ny / Reset
            </button>
          </div>

          {/* Search Section */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("search.customer")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    type="text"
                    id="search"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchError(null); // Clear error when user types
                    }}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 sm:text-sm ${
                      searchError 
                        ? "border-red-300 focus:ring-red-500 focus:border-red-500" 
                        : "border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                    }`}
                    placeholder={t("search.by.mobile.or.address")}
                    autoFocus
                  />
                </div>
                {searchError ? (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm font-medium text-red-800">
                      {t("search.error.title")}
                    </p>
                    <p className="text-sm text-red-700 mt-1">
                      {searchError}
                    </p>
                    <p className="text-xs text-red-600 mt-2">
                      {t("search.error.hint")}
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">
                    {t("search.by.mobile.or.address.hint")}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSearching || !searchQuery.trim()}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSearching ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("searching")}
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                    {t("search")}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Search Results (kun når flere treff — ett treff hopper rett til valgt kunde) */}
          {searchResults.length > 1 && !showCreateForm && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {t("search.results")}
              </h2>
              <div className="space-y-3">
                {searchResults.map((customer) => (
                  <div
                    key={customer.id}
                    onClick={() => setSelectedCustomer(customer)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedCustomer?.id === customer.id
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-gray-900">
                          {customer.displayTitle || customer.name}
                        </h3>
                        {customer.mobile && (
                          <p className="text-sm text-gray-500 mt-1">
                            <PhoneIcon className="h-4 w-4 inline mr-1 align-text-bottom" />
                            {customer.mobile}
                          </p>
                        )}
                        {customer.email && (
                          <p className="text-sm text-gray-500 mt-1">
                            <EnvelopeOpenIcon className="h-4 w-4 inline mr-1 align-text-bottom" />
                            {customer.email}
                          </p>
                        )}
                        {formatCustomerAddressLine(customer) && (
                          <p className="text-sm text-gray-500 mt-1">
                            <MapPinIcon className="h-4 w-4 inline mr-1 align-text-bottom" />
                            {formatCustomerAddressLine(customer)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCustomerProfileInNewTab(customer.id);
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                          title={t("customer.handler.open.customer.profile.hint")}
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-500" />
                          {t("customer.handler.open.customer.profile")}
                        </button>
                        {selectedCustomer?.id === customer.id && (
                          <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create Conversation Form (shown when no results or manually) */}
          {showCreateForm && (
            <div className="bg-white shadow rounded-lg p-6 mb-6">
              <div className="flex items-center mb-4">
                <UserPlusIcon className="h-5 w-5 text-indigo-600 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  {t("create.conversation")}
                </h2>
              </div>

              <Formik
                initialValues={{
                  name: formData.name || "",
                  mobile: formData.mobile || "",
                  email: formData.email || "",
                  address: formData.address || "",
                  postal: formData.postal || "",
                  place: formData.place || "",
                  conversation_date: formData.conversation_date?.split("T")[0] || new Date().toISOString().split("T")[0],
                  conversation_time: formData.conversation_time || new Date().toTimeString().slice(0, 5),
                  note: formData.note || "",
                  processed: formData.processed || false,
                }}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting }) => {

                  
                  // Prepare data with proper date/time format
                  const submissionData = {
                    name: values.name || "",
                    mobile: values.mobile || "",
                    email: values.email || "",
                    address: values.address || "",
                    postal: values.postal || "",
                    place: values.place || "",
                    conversation_date: values.conversation_date || formData.conversation_date,
                    conversation_time: values.conversation_time || formData.conversation_time,
                    note: values.note || "",
                    processed: values.processed || false,
                  };
                  

                  
                  // Call handler with Formik values directly
                  await handleCreateConversation(submissionData);
                  setSubmitting(false);
                }}
              >
                {({ values, setFieldValue, isSubmitting }) => (
                  <Form className="space-y-4">
                    {/* Customer Info */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <PMField
                        name="name"
                        label="name"
                        type="text"
                        placeholder="customer.name"
                        setFieldValue={setFieldValue}
                        values={values}
                      />

                      <PMField
                        name="mobile"
                        label="mobile"
                        type="tel"
                        placeholder="+47 123 45 678"
                        setFieldValue={setFieldValue}
                        values={values}
                      />

                      <PMField
                        name="email"
                        label="email"
                        type="email"
                        placeholder="kunde@example.com"
                        setFieldValue={setFieldValue}
                        values={values}
                      />

                      <PMField
                        name="address"
                        label="address"
                        type="text"
                        placeholder="street.address"
                        setFieldValue={setFieldValue}
                        values={values}
                      />

                      <PMField
                        name="postal"
                        label="postal.code"
                        type="text"
                        placeholder="0000"
                        setFieldValue={setFieldValue}
                        values={values}
                      />

                      <PMField
                        name="place"
                        label="place"
                        type="text"
                        placeholder="city"
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    </div>

                    {/* Conversation Date/Time */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          <ClockIcon className="h-4 w-4 inline mr-1" />
                          {t("conversation.date")}
                        </label>
                        <PMField
                          name="conversation_date"
                          label=""
                          type="date"
                          setFieldValue={setFieldValue}
                          values={values}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("conversation.time")}
                        </label>
                        <PMField
                          name="conversation_time"
                          label=""
                          type="time"
                          setFieldValue={setFieldValue}
                          values={values}
                        />
                      </div>
                    </div>

                    {/* Note */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <DocumentTextIcon className="h-4 w-4 inline mr-1" />
                        {t("note")}
                      </label>
                      <PMField
                        name="note"
                        label=""
                        multiline={true}
                        placeholder="conversation.note.placeholder"
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    </div>

                    {/* Processed checkbox */}
                    <div className="mb-4">
                      <PMField
                        name="processed"
                        label="mark.as.processed"
                        type="checkbox"
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting || (!values.name && !values.mobile)}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        {t("save")}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          {/* Selected Customer - Related Information */}
          {selectedCustomer && !showCreateForm && (
            <>
              {/* Valgt kunde — kontaktinfo og snarveier */}
              <div className="bg-white shadow rounded-lg p-6 mb-6 border border-gray-100">
                <CustomerLockBanner customer={selectedCustomer} className="mb-4" />
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {t("customer.handler.selected.section")}
                </h2>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-2">
                    <h3 className="text-base font-semibold text-gray-900">
                      {selectedCustomer.displayTitle || selectedCustomer.name}
                    </h3>
                    {formatCustomerAddressLine(selectedCustomer) && (
                      <p className="text-sm text-gray-600 flex gap-2">
                        <MapPinIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <span>{formatCustomerAddressLine(selectedCustomer)}</span>
                      </p>
                    )}
                    {selectedCustomer.email && (
                      <p className="text-sm text-gray-600 flex gap-2">
                        <EnvelopeOpenIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <span className="break-all">{selectedCustomer.email}</span>
                      </p>
                    )}
                    {selectedCustomer.mobile && (
                      <p className="text-sm text-gray-600 flex gap-2">
                        <PhoneIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
                        <span>{selectedCustomer.mobile}</span>
                      </p>
                    )}
                    {!formatCustomerAddressLine(selectedCustomer) &&
                      !selectedCustomer.email &&
                      !selectedCustomer.mobile && (
                        <p className="text-sm text-gray-500 italic">
                          {t("customer.handler.no.contact.info")}
                        </p>
                      )}
                  </div>
                  <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setCustomerEditDialogOpen(true)}
                      className="inline-flex items-center gap-2 rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                      {t("customer.handler.edit.customer")}
                    </button>
                    <button
                      type="button"
                      onClick={() => openCustomerProfileInNewTab(selectedCustomer.id)}
                      className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      title={t("customer.handler.open.customer.profile.hint")}
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-500" />
                      {t("customer.handler.open.customer.profile")}
                    </button>
                  </div>
                </div>
              </div>

              <CustomerDialog
                open={customerEditDialogOpen}
                onClose={handleCustomerEditDialogClose}
                selected={selectedCustomer}
                readOnly={true}
              />

              {/* Relatert informasjon */}
              <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-base font-medium text-gray-900 mb-4">
                  {t("customer.handler.related.information")}
                </h2>

                {/* Related Information Tabs/Sections */}
                {customerData.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <span className="ml-3 text-sm text-gray-500">{t("loading")}</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Communications */}
                    <div>
                      <div className="flex items-center mb-3">
                        <EnvelopeOpenIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="text-sm font-medium text-gray-900">
                          {t("communications")} ({customerData.communications.length})
                        </h3>
                      </div>
                      {customerData.communications.length > 0 ? (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {customerData.communications.map((comm) => {
                            const handler = comm.metadata?.handler || comm.processed_by_user?.name || t("unknown");
                            const isProcessed = comm.processed === true;
                            const isSelected = selectedCommunications.includes(comm.id);
                            const isEditing = editingCommunicationId === comm.id;
                            const canEditOwn = isOwnCustomerHandlerCommunication(comm);

                            return (
                              <div
                                key={comm.id}
                                className={`p-3 rounded-lg border ${
                                  isSelected 
                                    ? "bg-indigo-50 border-indigo-300" 
                                    : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                {comm.metadata?.service_appointment_reminder_id && <p className="mb-2 text-xs font-semibold text-slate-700">{t("serviceReminders.history")} · {t(`serviceReminders.state.${comm.metadata.state}`)}</p>}
                                {isEditing ? (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap text-xs text-gray-500">
                                      {comm.channel === "phone" && <PhoneIcon className="h-4 w-4" />}
                                      {comm.channel === "email" && <EnvelopeOpenIcon className="h-4 w-4" />}
                                      {comm.sent_at && (
                                        <span>{format(new Date(comm.sent_at), "dd.MM.yyyy HH:mm")}</span>
                                      )}
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t("customer.handler.communication.subject")}
                                      </label>
                                      <input
                                        type="text"
                                        value={communicationEditDraft.subject}
                                        onChange={(e) =>
                                          setCommunicationEditDraft((p) => ({ ...p, subject: e.target.value }))
                                        }
                                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        {t("customer.handler.communication.body")}
                                      </label>
                                      <textarea
                                        rows={5}
                                        value={communicationEditDraft.body_text}
                                        onChange={(e) =>
                                          setCommunicationEditDraft((p) => ({ ...p, body_text: e.target.value }))
                                        }
                                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                      />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        disabled={isSavingCommunicationEdit}
                                        onClick={handleSaveCommunicationEdit}
                                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                                      >
                                        {t("save")}
                                      </button>
                                      <button
                                        type="button"
                                        disabled={isSavingCommunicationEdit}
                                        onClick={() => {
                                          setEditingCommunicationId(null);
                                          setCommunicationEditDraft({ subject: "", body_text: "" });
                                        }}
                                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                      >
                                        {t("Cancel")}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-start gap-2 flex-1">
                                    {/* Checkbox for selection */}
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => toggleCommunicationSelection(comm.id)}
                                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <span className="text-xs font-medium text-gray-500">
                                          {comm.channel === "phone" && <PhoneIcon className="h-4 w-4 inline mr-1" />}
                                          {comm.channel === "email" && <EnvelopeOpenIcon className="h-4 w-4 inline mr-1" />}
                                          {comm.direction === "inbound" ? t("inbound") : t("outbound")}
                                        </span>
                                        {comm.sent_at && (
                                          <span className="text-xs text-gray-400">
                                            {format(new Date(comm.sent_at), "dd.MM.yyyy HH:mm")}
                                          </span>
                                        )}
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          isProcessed 
                                            ? "bg-green-100 text-green-700" 
                                            : "bg-yellow-100 text-yellow-700"
                                        }`}>
                                          {isProcessed ? t("processed") : t("unprocessed")}
                                        </span>
                                      </div>
                                      {comm.subject && (
                                        <p className="text-sm font-medium text-gray-900 mb-1">
                                          {comm.subject}
                                        </p>
                                      )}
                                      {comm.body_text && (
                                        <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                                          {comm.body_text}
                                        </p>
                                      )}
                                      <p className="text-xs text-gray-500">
                                        {t("created.by")}: {handler}
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Action buttons */}
                                  <div className="flex gap-2 flex-col">
                                    {canEditOwn && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingCommunicationId(comm.id);
                                          setCommunicationEditDraft({
                                            subject: typeof comm.subject === "string" ? comm.subject : "",
                                            body_text: typeof comm.body_text === "string" ? comm.body_text : "",
                                          });
                                        }}
                                        className="text-xs text-gray-700 hover:text-indigo-700 px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1"
                                        title={t("customer.handler.edit.communication")}
                                      >
                                        <PencilSquareIcon className="h-3.5 w-3.5" />
                                        {t("customer.handler.edit.communication")}
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => setCommunicationDetailDialog(comm)}
                                      className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded border border-indigo-300 hover:bg-indigo-50"
                                    >
                                      {t("see.more")}
                                    </button>
                                    {!isProcessed && (
                                      <button
                                        type="button"
                                        onClick={() => handleMarkAsProcessed(comm.id, true)}
                                        className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded border border-green-300 hover:bg-green-50"
                                      >
                                        {t("mark.as.processed")}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">{t("no.communications")}</p>
                      )}
                      
                      {/* Selected communications actions */}
                      {selectedCommunications.length > 0 && (
                        <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                          <p className="text-sm text-indigo-900 mb-2">
                            {t("selected.communications")}: {selectedCommunications.length}
                          </p>
                          <button
                            onClick={() => {
                              setCaseChoiceDialogOpen(true);
                            }}
                            className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded"
                          >
                            {t("link.to.case")}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Cases */}
                    <div>
                      <div className="flex items-center mb-3">
                        <EnvelopeOpenIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="text-sm font-medium text-gray-900">
                          {t("cases")} ({customerData.cases.length})
                        </h3>
                      </div>
                      {customerData.cases.length > 0 ? (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {customerData.cases.map((caseItem) => (
                            <div
                              key={caseItem.id}
                              className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <a
                                      href={`/dashboard/cases/${caseItem.id}`}
                                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                                    >
                                      {caseItem.displayTitle || caseItem.case_number}
                                    </a>
                                    {caseItem.status && (
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        caseItem.status === "CLOSED" ? "bg-gray-100 text-gray-700" :
                                        caseItem.status === "PROCESSING" ? "bg-yellow-100 text-yellow-700" :
                                        "bg-blue-100 text-blue-700"
                                      }`}>
                                        {caseItem.status}
                                      </span>
                                    )}
                                  </div>
                                  {caseItem.title && (
                                    <p className="text-xs text-gray-600">{caseItem.title}</p>
                                  )}
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedCase(caseItem);
                                    setCaseDialogOpen(true);
                                  }}
                                  className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded border border-indigo-300 hover:bg-indigo-50 ml-2"
                                >
                                  {t("see.more")}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">{t("no.cases")}</p>
                      )}
                    </div>

                    {/* Events */}
                    <div>
                      <div className="flex items-center mb-3">
                        <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="text-sm font-medium text-gray-900">
                          {t("upcoming.events")} ({customerData.events.filter((event) => {
                            const eventDate = new Date(event.start_time);
                            const now = new Date();
                            const twoMonthsFromNow = new Date(now);
                            twoMonthsFromNow.setMonth(now.getMonth() + 2);
                            return eventDate >= now && eventDate <= twoMonthsFromNow;
                          }).length})
                        </h3>
                      </div>
                      {(() => {
                        const filteredEvents = customerData.events.filter((event) => {
                          // Show events from today to 2 months in the future
                          const eventDate = new Date(event.start_time);
                          const now = new Date();
                          const twoMonthsFromNow = new Date(now);
                          twoMonthsFromNow.setMonth(now.getMonth() + 2);
                          return eventDate >= now && eventDate <= twoMonthsFromNow;
                        });
                        return filteredEvents.length > 0 ? (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {filteredEvents.map((event) => (
                              <div
                                key={event.id}
                                className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-medium text-gray-900">
                                        {event.title}
                                      </span>
                                      {event.status && (
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          event.status === "finished" ? "bg-gray-100 text-gray-700" :
                                          event.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                                          "bg-blue-100 text-blue-700"
                                        }`}>
                                          {event.status}
                                        </span>
                                      )}
                                    </div>
                                    {event.start_time && (
                                      <p className="text-xs text-gray-600">
                                        <ClockIcon className="h-3 w-3 inline mr-1" />
                                        {format(new Date(event.start_time), "dd.MM.yyyy HH:mm")}
                                      </p>
                                    )}
                                    {(event.related || event.module_details) && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        <LinkIcon className="h-3 w-3 inline mr-1" />
                                        {(event.related_type === "Report" || event.module_type === "report") &&
                                         (event.related?.report_number || event.module_details?.report_number) && (
                                          <button
                                            onClick={() => handleOpenReportDialog(event.related?.id || event.module_id)}
                                            className="text-indigo-600 hover:text-indigo-800 underline"
                                          >
                                            {t("report")} {event.related?.report_number || event.module_details?.report_number}
                                          </button>
                                        )}
                                        {(event.related_type === "Dashboard::Case" || event.related_type === "Case" || event.module_type === "case") && 
                                         (event.related?.case_number || event.module_details?.case_number) && (
                                          <a
                                            href={`/dashboard/cases/${event.related?.id || event.module_id}`}
                                            className="text-indigo-600 hover:text-indigo-800"
                                          >
                                            {t("case")} {event.related?.case_number || event.module_details?.case_number}
                                          </a>
                                        )}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => {
                                      setSelectedEvent(event);
                                      setEventDialogOpen(true);
                                    }}
                                    className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded border border-indigo-300 hover:bg-indigo-50 ml-2"
                                  >
                                    {t("see.more")}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">{t("no.events")}</p>
                        );
                      })()}
                    </div>

                    {/* Reports */}
                    <div>
                      <div className="flex items-center mb-3">
                        <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <h3 className="text-sm font-medium text-gray-900">
                          Rapporter ({customerReports.length})
                        </h3>
                      </div>
                      {customerReports.length > 0 ? (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {customerReports.map((report) => (
                            <div
                              key={report.id}
                              onClick={() => handleOpenReportDialog(report.id)}
                              className="p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    {report.report_type && (
                                      <span className="text-xs text-gray-500">
                                        {report.report_type.displayTitle || report.report_type.title}
                                      </span>
                                    )}
                                    <span className="text-sm font-medium text-gray-900">
                                      {report.displayTitle || report.report_number}
                                    </span>
                                  </div>
                                  {report.description && (
                                    <div
                                      className="text-xs text-gray-600 mt-1 line-clamp-2"
                                      dangerouslySetInnerHTML={{
                                        __html: report.description.length > 100
                                          ? report.description.substring(0, 100) + "..."
                                          : report.description
                                      }}
                                    />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic">
                          Ingen rapporter funnet for denne kunden.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Report Overview Dialog */}
              <ReportOverviewSummary
                open={reportDialogOpen}
                onClose={handleCloseReportDialog}
                reportId={selectedReportId}
                currentUser={currentUser}
              />

              {/* Add Communication Section */}
              <div className="bg-white shadow rounded-lg p-6">

              {/* Conversation Date/Time */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    <ClockIcon className="h-4 w-4 inline mr-1" />
                    {t("conversation.date")}
                  </label>
                  <input
                    type="date"
                    value={formData.conversation_date.split("T")[0]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        conversation_date: `${e.target.value}T${formData.conversation_time}`,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("conversation.time")}
                  </label>
                  <input
                    type="time"
                    value={formData.conversation_time}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        conversation_time: e.target.value,
                        conversation_date: `${formData.conversation_date.split("T")[0]}T${e.target.value}`,
                      })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Communication Note */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DocumentTextIcon className="h-4 w-4 inline mr-1" />
                  {t("add.communication")}
                </label>
                <textarea
                  rows={4}
                  value={formData.note}
                  onChange={(e) =>
                    setFormData({ ...formData, note: e.target.value })
                  }
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder={t("conversation.note.placeholder")}
                />
              </div>

              {/* Processed checkbox */}
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.processed}
                    onChange={(e) =>
                      setFormData({ ...formData, processed: e.target.checked })
                    }
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t("mark.as.processed")}
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end">
                <button
                  onClick={handleAddCommunication}
                  disabled={!formData.note}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {t("save")}
                </button>
              </div>
              </div>
            </>
          )}
            </div>
            
            {/* Right Column - Treatment Section */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg p-6 sticky top-6">
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FunnelIcon className="h-5 w-5 text-indigo-600" />
                    {t("treatment.section")}
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("treatment.section.description")}
                  </p>
                </div>

                {/* Search and Filters */}
                <div className="space-y-3 mb-4">
                  {/* Search */}
                  <div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={communicationSearchQuery}
                        onChange={(e) => setCommunicationSearchQuery(e.target.value)}
                        className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t("search.communications")}
                      />
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={communicationFilter}
                      onChange={(e) => setCommunicationFilter(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-xs focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="all">{t("all.statuses")}</option>
                      <option value="unprocessed">{t("unprocessed")}</option>
                      <option value="processed">{t("processed")}</option>
                    </select>
                    <select
                      value={communicationChannelFilter}
                      onChange={(e) => setCommunicationChannelFilter(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-xs focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="all">{t("all.channels")}</option>
                      <option value="phone">{t("phone")}</option>
                      <option value="email">{t("email")}</option>
                      <option value="sms">{t("sms")}</option>
                    </select>
                  </div>
                </div>

                {/* Communications List */}
                <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {isLoadingAllCommunications ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                      <span className="ml-2 text-sm text-gray-500">{t("loading")}</span>
                    </div>
                  ) : isLoadingCommunication ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-sm text-gray-500">{t("loading.customer")}</span>
                      </div>
                    </div>
                  ) : filteredCommunications.length > 0 ? (
                    filteredCommunications.map((comm) => {
                      const isProcessed = comm.processed === true;
                      const customer = comm.customer;
                      
                      return (
                        <div
                          key={comm.id}
                          onClick={() => {
                            if (!isLoadingCommunication) {
                              handleSelectCommunicationForTreatment(comm);
                            }
                          }}
                          className={`p-3 rounded-lg border transition-colors ${
                            isLoadingCommunication
                              ? "opacity-50 cursor-not-allowed pointer-events-none"
                              : "cursor-pointer"
                          } ${
                            isProcessed
                              ? "bg-gray-50 border-gray-200 hover:border-gray-300"
                              : "bg-yellow-50 border-yellow-200 hover:border-yellow-300"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {comm.channel === "phone" && <PhoneIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
                                {comm.channel === "email" && <EnvelopeOpenIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />}
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  isProcessed
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}>
                                  {isProcessed ? t("processed") : t("unprocessed")}
                                </span>
                                {comm.sent_at && (
                                  <span className="text-xs text-gray-400">
                                    {format(new Date(comm.sent_at), "dd.MM HH:mm")}
                                  </span>
                                )}
                              </div>
                              {customer && (
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {customer.displayTitle || customer.name}
                                </p>
                              )}
                              {comm.subject && (
                                <p className="text-xs text-gray-600 truncate mt-1">
                                  {comm.subject}
                                </p>
                              )}
                              {comm.body_text && (
                                <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                                  {comm.body_text}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      {t("no.communications.found")}
                    </p>
                  )}
                </div>

                {/* Summary */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    {t("showing")} {filteredCommunications.length} {t("of")} {allCommunications.length} {t("communications")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Communication Detail Dialog */}
      {communicationDetailDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setCommunicationDetailDialog(null)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{communicationDetailDialog.subject}</h3>
              <div className="flex items-center gap-2">
                {selectedCustomer?.id && communicationDetailDialog?.id && (
                  <button
                    type="button"
                    disabled={exportingCommPdf}
                    onClick={async () => {
                      setExportingCommPdf(true);
                      try {
                        const res = await fetchCommuncationPdf({
                          id: communicationDetailDialog.id,
                          customer_id: selectedCustomer.id,
                        });
                        if (res?.pdf_url) {
                          window.open(res.pdf_url, "_blank", "noopener");
                        } else {
                          toast.error(t("customer.communication.pdf_failed"));
                        }
                      } catch {
                        toast.error(t("customer.communication.pdf_failed"));
                      } finally {
                        setExportingCommPdf(false);
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    {exportingCommPdf
                      ? t("customer.communication.pdf_loading")
                      : t("customer.communication.export_pdf")}
                  </button>
                )}
              <button
                onClick={() => setCommunicationDetailDialog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium">{t("channel")}:</span>
                <span>{communicationDetailDialog.channel}</span>
                <span className="mx-2">•</span>
                <span className="font-medium">{t("direction")}:</span>
                <span>{communicationDetailDialog.direction === "inbound" ? t("inbound") : t("outbound")}</span>
              </div>
              {communicationDetailDialog.sent_at && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{t("sent.at")}:</span>{" "}
                  {format(new Date(communicationDetailDialog.sent_at), "dd.MM.yyyy HH:mm")}
                </div>
              )}
              {communicationDetailDialog.body_text && (
                <div className="text-sm text-gray-900 whitespace-pre-wrap border-t pt-3">
                  {communicationDetailDialog.body_text}
                </div>
              )}
              {communicationDetailDialog.metadata && (
                <div className="text-xs text-gray-500 border-t pt-3">
                  <pre className="whitespace-pre-wrap">{JSON.stringify(communicationDetailDialog.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Case Dialog */}
      <CaseDialog
        open={caseDialogOpen}
        onClose={() => {
          setCaseDialogOpen(false);
          setSelectedCase(null);
        }}
        selected={selectedCase || { id: null }}
        customer={selectedCustomer}
        onRefresh={async (newCase) => {
          // Refresh cases after case is created/updated
          if (selectedCustomer) {
            const casesResponse = await fetchCases({
              filters: { 0: { name: "customer", value: selectedCustomer.id.toString() } },
              per_page: 10,
              page: 1,
              sortBy: "created_at",
              sort: "desc",
              fields: "id,case_number,displayTitle,title,status",
            });
            setCustomerData((prev) => ({
              ...prev,
              cases: filterEmptyObjects(casesResponse.data || []),
            }));
            
            // If a case was created/updated and we have selected communications, link them
            const caseId = newCase?.id || selectedCase?.id;
            if (selectedCommunications.length > 0 && caseId) {
              // Link communications and open case in new tab
              await handleLinkToCase(caseId, true);
              setSelectedCase(newCase || selectedCase);
              // Close dialog after linking
              setCaseDialogOpen(false);
            } else if (caseId) {
              // Even if no communications selected, open case in new tab when created
              const caseUrl = `/dashboard/cases/${caseId}`;
              window.open(caseUrl, '_blank');
            }
          }
        }}
      />
      
      {/* Case Choice Dialog - Choose between new or existing case */}
      {caseChoiceDialogOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setCaseChoiceDialogOpen(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t("link.to.case")}</h3>
              <button
                onClick={() => setCaseChoiceDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {t("link.to.case.choice.description")}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  onClick={() => {
                    setCaseChoiceDialogOpen(false);
                    setCaseSearchDialogOpen(true);
                  }}
                  className="p-4 border-2 border-indigo-300 rounded-lg hover:bg-indigo-50 hover:border-indigo-500 transition-colors text-left"
                >
                  <div className="flex items-center mb-2">
                    <MagnifyingGlassIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    <span className="font-medium text-gray-900">{t("link.to.existing.case")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("link.to.existing.case.description")}</p>
                </button>
                <button
                  onClick={() => {
                    setCaseChoiceDialogOpen(false);
                    setCaseDialogOpen(true);
                    setSelectedCase(null); // New case
                  }}
                  className="p-4 border-2 border-indigo-300 rounded-lg hover:bg-indigo-50 hover:border-indigo-500 transition-colors text-left"
                >
                  <div className="flex items-center mb-2">
                    <DocumentTextIcon className="h-5 w-5 text-indigo-600 mr-2" />
                    <span className="font-medium text-gray-900">{t("create.new.case")}</span>
                  </div>
                  <p className="text-sm text-gray-600">{t("create.new.case.description")}</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Case Search Dialog - Search and select existing case */}
      {caseSearchDialogOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" onClick={() => {
          if (isLinkingToCase) return; // Prevent closing while linking
          setCaseSearchDialogOpen(false);
          setCaseSearchQuery("");
          setCaseSearchResults([]);
          setCustomerCasesLoaded(false);
          setIsLinkingToCase(false);
        }}>
          <div className="relative mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t("link.to.existing.case")}</h3>
              <button
                onClick={() => {
                  if (isLinkingToCase) return; // Prevent closing while linking
                  setCaseSearchDialogOpen(false);
                  setCaseSearchQuery("");
                  setCaseSearchResults([]);
                  setCustomerCasesLoaded(false);
                  setIsLinkingToCase(false);
                }}
                disabled={isLinkingToCase}
                className={`text-gray-400 hover:text-gray-600 ${isLinkingToCase ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              {/* Loading indicator */}
              {isLinkingToCase && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-3">
                    <svg
                      className="animate-spin h-8 w-8 text-indigo-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <p className="text-sm text-gray-600">{t("linking.communications")}</p>
                  </div>
                </div>
              )}

              {/* Customer's Cases - Show first */}
              {!isLinkingToCase && caseSearchResults.length > 0 && !caseSearchQuery && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    {t("customer.cases")} ({caseSearchResults.length})
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {caseSearchResults.map((caseItem) => (
                      <div
                        key={caseItem.id}
                        className={`p-3 border rounded-lg transition-colors ${
                          isLinkingToCase 
                            ? "opacity-50 cursor-not-allowed" 
                            : "hover:bg-gray-50 cursor-pointer"
                        }`}
                        onClick={async () => {
                          if (isLinkingToCase) return;
                          // Link communications to selected case
                          await handleLinkToCase(caseItem.id, true);
                          setCaseSearchDialogOpen(false);
                          setCaseSearchQuery("");
                          setCaseSearchResults([]);
                          setCustomerCasesLoaded(false);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {caseItem.displayTitle || caseItem.case_number}
                              </span>
                              {caseItem.status && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  caseItem.status === "CLOSED" ? "bg-gray-100 text-gray-700" :
                                  caseItem.status === "PROCESSING" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-blue-100 text-blue-700"
                                }`}>
                                  {caseItem.status}
                                </span>
                              )}
                            </div>
                            {caseItem.title && (
                              <p className="text-xs text-gray-600">{caseItem.title}</p>
                            )}
                          </div>
                          <LinkIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 ml-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search Input */}
              {!isLinkingToCase && (
                <div className={caseSearchResults.length > 0 && !caseSearchQuery ? "border-t pt-4" : ""}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("search.case")}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={caseSearchQuery}
                        onChange={(e) => setCaseSearchQuery(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearchCases();
                          }
                        }}
                        disabled={isLinkingToCase}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder={t("search.case.placeholder")}
                      />
                    </div>
                    <button
                      onClick={handleSearchCases}
                      disabled={isSearchingCases || isLinkingToCase}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearchingCases ? t("searching") : t("search")}
                    </button>
                  </div>
                </div>
              )}

              {/* Search Results - Show when user searches */}
              {!isLinkingToCase && caseSearchQuery && caseSearchResults.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    {t("search.results")} ({caseSearchResults.length})
                  </h4>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {caseSearchResults.map((caseItem) => (
                      <div
                        key={caseItem.id}
                        className={`p-3 border rounded-lg transition-colors ${
                          isLinkingToCase 
                            ? "opacity-50 cursor-not-allowed" 
                            : "hover:bg-gray-50 cursor-pointer"
                        }`}
                        onClick={async () => {
                          if (isLinkingToCase) return;
                          // Link communications to selected case
                          await handleLinkToCase(caseItem.id, true);
                          setCaseSearchDialogOpen(false);
                          setCaseSearchQuery("");
                          setCaseSearchResults([]);
                          setCustomerCasesLoaded(false);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {caseItem.displayTitle || caseItem.case_number}
                              </span>
                              {caseItem.status && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  caseItem.status === "CLOSED" ? "bg-gray-100 text-gray-700" :
                                  caseItem.status === "PROCESSING" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-blue-100 text-blue-700"
                                }`}>
                                  {caseItem.status}
                                </span>
                              )}
                            </div>
                            {caseItem.title && (
                              <p className="text-xs text-gray-600">{caseItem.title}</p>
                            )}
                            {caseItem.customer && (
                              <p className="text-xs text-gray-500 mt-1">
                                {t("customer")}: {caseItem.customer.displayTitle || caseItem.customer.name}
                              </p>
                            )}
                          </div>
                          <LinkIcon className="h-5 w-5 text-indigo-600 flex-shrink-0 ml-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isLinkingToCase && caseSearchQuery && !isSearchingCases && caseSearchResults.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-500">
                  {t("no.cases.found")}
                </div>
              )}

              {!isLinkingToCase && !caseSearchQuery && caseSearchResults.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-500">
                  {t("no.cases.found")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Event Dialog - Simple version for now */}
      {eventDialogOpen && selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => {
          setEventDialogOpen(false);
          setSelectedEvent(null);
        }}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{selectedEvent.title}</h3>
              <button
                onClick={() => {
                  setEventDialogOpen(false);
                  setSelectedEvent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              {selectedEvent.start_time && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{t("start.time")}:</span>{" "}
                  {format(new Date(selectedEvent.start_time), "dd.MM.yyyy HH:mm")}
                </div>
              )}
              {selectedEvent.end_time && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{t("end.time")}:</span>{" "}
                  {format(new Date(selectedEvent.end_time), "dd.MM.yyyy HH:mm")}
                </div>
              )}
              {selectedEvent.description && (
                <div className="text-sm text-gray-900 whitespace-pre-wrap border-t pt-3">
                  {selectedEvent.description}
                </div>
              )}
              {selectedEvent.related && (
                <div className="text-sm text-gray-600 border-t pt-3">
                  <span className="font-medium">{t("related")}:</span>{" "}
                  {selectedEvent.related_type} - {selectedEvent.related?.displayTitle || selectedEvent.related?.id}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </RoleOnly>
  );
};

export default CustomerHandler;


